import React, { createRef, forwardRef, useEffect, useState } from "react";
import { Modal, Portal, useTheme } from "react-native-paper";
import { Platform, StyleSheet, View, Dimensions } from "react-native";
import {
  Button,
  ErrorMessage,
  FormTitle,
  InfoBox,
  InputAccessoryNavigation,
  Screen,
  Text,
  TextInput,
} from "../../components";
import { Stack } from "../../components/layout/stack";
import { useNavigation } from "@react-navigation/native";
import { ThemeType } from "../../theme/theme";
import { useForm } from "react-hook-form";
import { translate } from "../../i18n";
import { Inline } from "../../components/layout/inline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { addBankAccountSchema } from "../wallet/schemas";
import {
  accountTypeToDBFieldValueMapping,
  BankAccountTypeDropdown,
} from "../../components/dropdown/bank-account-type-dropdown";
import { PaymentSourceService } from "../../api";
import { BankAccountFields, InfoBoxIconType, InfoBoxType } from "../../enums";
import { AntDesign } from "@expo/vector-icons";
import { setDefaultPaymentSourceUpdated } from "../../redux/actions/actionCreators";
import { RootState } from "../../redux/reducers";
import { testProps } from "../../utils/testProps";

const { ACCOUNT_HOLDER, ACCOUNT_TYPE, ROUTING_NUMBER, ACCOUNT_NUMBER, CONFIRM_ACCOUNT_NUMBER } =
  BankAccountFields;

interface IProps {
  route: any;
}

const windowWidth = Dimensions.get("window").width;
let pagePaddingWidth = 0;

if (windowWidth > 1024) {
  pagePaddingWidth = windowWidth * 0.1;
}
if (windowWidth > 1260) {
  pagePaddingWidth = windowWidth * 0.3;
}

export const AddBankAccount = ({ route }: IProps) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const theme = useTheme() as ThemeType;
  const { spacing } = theme;
  const styles = getStyles(theme);
  const [loading, setLoading] = useState(false);
  const { params = {} } = route;
  const { fromSelectPayment, reservationId, propertyUnit, amounts } = params;
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addBankAccountSchema),
  });
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);
  const [apiError, setApiError] = React.useState<any>(false);
  const service = new PaymentSourceService(cognitoUser, dispatch);
  const inputAccessoryViewID = "inputAccessoryViewID2";
  const ref = [createRef(), createRef(), createRef(), createRef(), createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const logger = useSelector((state: RootState) => state.logger);

  useEffect(() => {
    register(ACCOUNT_HOLDER);
    register(ACCOUNT_TYPE);
    register(ROUTING_NUMBER);
    register(ACCOUNT_NUMBER);
    register(CONFIRM_ACCOUNT_NUMBER);
  }, [register]);

  const onSubmit = async (data: any) => {
    setLoading(true);
    const {
      routingNumber: paymentRoutingNumber,
      accountNumber: bankAccountNumber,
      accountType: bankAccountType,
      accountHolder,
    } = data;
    service
      .addPaymentMethodBA({
        ...data,
        bankAccountType: accountTypeToDBFieldValueMapping(bankAccountType),
        cardholder: accountHolder,
        allowCof: true,
        reservationId,
        paymentSourceType: "BANK_ACCOUNT",
        paymentRoutingNumber,
        bankAccountNumber,
      })
      .then((resp) => {
        const { paymentSourceId } = resp;
        dispatch(setDefaultPaymentSourceUpdated(paymentSourceId));
        setLoading(false);
        return navigation.goBack();
      })
      .catch((e) => {
        logger.error(e);
        const { message } = e;
        setApiError(message);
        setLoading(false);
      });
    return;
  };

  return (
    <Screen
      style={styles.container}
      preset="scroll"
      statusBar="dark-content"
      unsafe
      dontUseAutoPadding
      keyboardOffset="keyboardNavigation"
      backgroundColor={theme.colors.background}>
      <Stack space="medium">
        <FormTitle tx="homeStack.bankAccountDetails"></FormTitle>
        <Stack horizontalSpace="medium">
          <Stack>
            <View accessible={true}>
              <InputRow
                styles={styles}
                setValue={setValue}
                errors={errors}
                formName="accountHolder"
                inputAccessoryViewID={inputAccessoryViewID}
                ref={ref[0] as any}
                onFocus={() => setCurrentInputIndex(0)}
                onSubmitEditing={() => (ref[1].current as any).focus()}
                returnKeyType="next"
              />
              <View accessible={true}>
                <Text tx="walletStack.accountType" style={styles.label} />
                <BankAccountTypeDropdown
                  styles={styles}
                  errors={errors}
                  // without this samsung breaks
                  label={Platform.OS === "ios" ? undefined : "Select a type..."}
                  formName={ACCOUNT_TYPE}
                  setValue={(value: any) => setValue(ACCOUNT_TYPE, value)}
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={ref[1] as any}
                  onFocus={() => setCurrentInputIndex(1)}
                  onSubmitEditing={() => (ref[2].current as any).focus()}
                />
                <ErrorMessage errors={errors} formName={ACCOUNT_TYPE} />
              </View>
              <InputRow
                styles={styles}
                setValue={setValue}
                errors={errors}
                keyboardType="number-pad"
                maxLength={9}
                formName={ROUTING_NUMBER}
                hasInfoDialog
                infoDialogName={ROUTING_NUMBER}
                inputAccessoryViewID={inputAccessoryViewID}
                ref={ref[2] as any}
                onFocus={() => setCurrentInputIndex(2)}
                onSubmitEditing={() => (ref[3].current as any).focus()}
                returnKeyType="next"
              />
              <InputRow
                styles={styles}
                setValue={setValue}
                keyboardType="number-pad"
                errors={errors}
                maxLength={20}
                formName={ACCOUNT_NUMBER}
                hasInfoDialog
                infoDialogName={ACCOUNT_NUMBER}
                inputAccessoryViewID={inputAccessoryViewID}
                ref={ref[3] as any}
                onFocus={() => setCurrentInputIndex(3)}
                onSubmitEditing={() => (ref[4].current as any).focus()}
                returnKeyType="next"
              />
              <InputRow
                styles={styles}
                setValue={setValue}
                keyboardType="number-pad"
                errors={errors}
                maxLength={20}
                formName={CONFIRM_ACCOUNT_NUMBER}
                inputAccessoryViewID={inputAccessoryViewID}
                ref={ref[4] as any}
                onFocus={() => setCurrentInputIndex(4)}
                onSubmitEditing={handleSubmit(onSubmit)}
                returnKeyType="go"
              />
              {Platform.OS === "ios" ? (
                <InputAccessoryNavigation
                  inputAccessoryViewID={inputAccessoryViewID}
                  ref={ref as any}
                  currentInputIndex={currentInputIndex}
                />
              ) : null}
            </View>
            <AchNoticeBox />
            {apiError ? (
              <InfoBox
                width="100%"
                headline={translate("homeStack.error")}
                infoBoxType={InfoBoxType.RED}
                usesXClose={true}
                xCloseHandler={() => setApiError(null)}
                headlineIconType={InfoBoxIconType.RED_X}>
                <Text>{apiError}</Text>
              </InfoBox>
            ) : null}
          </Stack>
          <View accessible={true}>
            <View style={styles.buttonContainer}>
              <Button
                mode="contained"
                tx="walletStack.done"
                loading={loading}
                disabled={loading}
                onPress={handleSubmit(onSubmit)}
                {...testProps("continue-button")}
              />
            </View>
          </View>
        </Stack>
      </Stack>
    </Screen>
  );
};

interface InputRowProps {
  styles: any;
  errors?: Record<string, any>;
  setValue: any;
  formName: string;
  hasInfoDialog?: boolean;
  infoDialogName?: string;
  maxLength?: number;
  render?: any;
  keyboardType?: any;
  placeholder?: string;
  returnKeyType?:
    | "default"
    | "none"
    | "done"
    | "go"
    | "next"
    | "search"
    | "send"
    | "previous"
    | "google"
    | "join"
    | "route"
    | "yahoo"
    | "emergency-call"
    | undefined;
  inputAccessoryViewID?: string;
  onFocus?: any;
  onSubmitEditing?: any;
}

const InputRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      hasInfoDialog,
      infoDialogName,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
    }: InputRowProps,
    ref: any
  ) => {
    return (
      <View accessible={true}>
        <Inline style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
          <Text tx={`walletStack.${formName}`} style={styles.label} />
          {hasInfoDialog && <InfoDialog name={infoDialogName} styles={styles} />}
        </Inline>

        <TextInput
          error={errors?.[formName]?.message}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
          {...testProps(formName)}
        />
        <ErrorMessage errors={errors} formName={formName} />
      </View>
    );
  }
);

const InfoDialog = ({ styles, name }: any) => {
  const { iconSize, colors } = useTheme() as ThemeType;
  const [isModalVisible, setModalVisible] = useState(false);

  const modalTitle =
    name === ACCOUNT_NUMBER ? "walletStack.accountNumber" : "walletStack.routingNumber";
  const modalInfo =
    name === ACCOUNT_NUMBER ? "walletStack.accountNumberInfo" : "walletStack.routingNumberInfo";

  return (
    <>
      <View style={styles.infoLinkContainer}>
        <AntDesign
          accessible={true}
          accessibilityLabel={
            name === ACCOUNT_NUMBER
              ? translate("walletStack.accountNumberInfoIcon")
              : translate("walletStack.routingNumberInfoIcon")
          }
          name={"exclamationcircleo"}
          size={iconSize.small}
          color={colors.darkBg}
        />
        <Text
          tx="homeStack.learnMore"
          onPress={() => setModalVisible(true)}
          style={styles.infoLink}
        />
      </View>
      <Portal>
        <Modal
          visible={isModalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={styles.infoModalContainer}>
          <Stack space="small" horizontalSpace="tiny">
            <Stack space="small" style={styles.infoModalContent}>
              <Text tx={modalTitle} style={styles.infoModalTitle} />
              <Text tx={modalInfo} />
            </Stack>
            <View style={styles.buttonContainerSmaller}>
              <Button
                onPress={() => setModalVisible(false)}
                mode="contained"
                style={styles.infoModalButton}>
                {translate("homeStack.close")}
              </Button>
            </View>
          </Stack>
        </Modal>
      </Portal>
    </>
  );
};

const AchNoticeBox = () => (
  <InfoBox
    headline={translate("walletStack.achNoticeTitle")}
    infoBoxType={InfoBoxType.BLUE}
    iconName={translate("walletStack.achNoticeIcon")}>
    <Text tx="walletStack.achNoticeMessage" />
  </InfoBox>
);

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      ...Platform.select({
        native: {
          backgroundColor: colors.background,
          paddingTop: spacing.small,
          paddingBottom: spacing.small,
        },
        default: {
          maxHeight: 250,
          paddingLeft: pagePaddingWidth,
          paddingRight: pagePaddingWidth,
        },
      }),
    },
    sectionTitle: {
      fontSize: fontSize.large,
      fontWeight: "bold",
      marginTop: spacing.large,
      marginBottom: spacing.tiny,
    },
    label: {
      fontSize: fontSize.large,
      marginLeft: spacing.smaller,
      marginBottom: spacing.tiny,
      marginTop: spacing.small,
    },
    spaceBetween: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    marginHorizontal: {
      marginHorizontal: spacing.small,
    },
    infoLinkContainer: { display: "flex", flexDirection: "row", alignItems: "center" },
    infoLink: {
      marginLeft: spacing.tiny,
      textDecorationLine: "underline",
      fontSize: fontSize.medium,
    },
    infoModalContainer: {
      display: "flex",
      padding: spacing.medium,
      marginHorizontal: spacing.medium,
      backgroundColor: colors.background,
      borderRadius: 10,
      flexDirection: "column",
    },
    infoModalContent: {
      padding: spacing.smaller,
    },
    infoModalTitle: {
      fontWeight: "bold",
    },
    infoModalButton: {
      width: "60%",
      marginTop: spacing.large,
      alignSelf: "flex-end",
    },
    buttonContainer: {
      ...Platform.select({
        default: {
          width: 325,
          marginLeft: "auto",
          marginRight: "auto",
          marginBottom: 20,
        },
      }),
    },
    buttonContainerSmaller: {
      ...Platform.select({
        default: {
          width: 275,
          marginLeft: "auto",
          marginRight: "auto",
        },
      }),
    },
  });
};
